import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/videos';

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();

  const video = useMemo(() => videos.find(v => v.id === id), [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter(v => v.id !== video.id && v.categorySlug === video.categorySlug)
      .slice(0, 4);
  }, [video]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-video-off-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该视频可能已被移除或链接无效</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.publishDate,
    author: {
      '@type': 'Organization',
      name: '91视频网站',
    },
    publisher: {
      '@type': 'Organization',
      name: '91视频网站',
    },
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 91视频网站高清在线观看`}
        description={video.description.substring(0, 150)}
        keywords={`91视频网站,${video.title},${video.category},91视频网站在线观看,高清视频网站推荐`}
        canonicalUrl={`https://91video.example.com/video/${video.id}`}
        ogType="article"
        ogTitle={video.title}
        ogDescription={video.description.substring(0, 150)}
        ogImage={video.thumbnail}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: video.category, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 pb-14">
            <article className="flex-1">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-black mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 91视频网站高清播放`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 cursor-pointer transition-all duration-200 hover:scale-110">
                    <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                  </div>
                </div>
              </div>

              <header>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
                  {video.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-foreground-500">
                  <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 font-medium">
                    {video.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万次播放` : `${video.views}次播放`}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {video.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {video.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {video.publishDate}
                  </span>
                </div>

                {video.tags && video.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {video.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </header>

              <div className="bg-background-100 rounded-xl p-5 mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-3">视频简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {video.description}
                </p>

                {(video.director || video.cast) && (
                  <div className="mt-4 pt-4 border-t border-background-200/60 space-y-2">
                    {video.director && (
                      <p className="text-sm text-foreground-500">
                        <strong className="text-foreground-700 font-medium">导演：</strong>
                        {video.director}
                      </p>
                    )}
                    {video.cast && video.cast.length > 0 && (
                      <p className="text-sm text-foreground-500">
                        <strong className="text-foreground-700 font-medium">主演：</strong>
                        {video.cast.join(' / ')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i> 点赞
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
            </article>

            <aside className="lg:w-80 flex-shrink-0">
              <h3 className="text-lg font-heading text-foreground-900 mb-4">相关推荐</h3>
              <div className="space-y-4" data-product-shop>
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/video/${rv.id}`}
                    className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200 group"
                  >
                    <div className="relative w-36 flex-shrink-0 aspect-video rounded-lg overflow-hidden">
                      <img
                        src={rv.thumbnail}
                        alt={rv.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px]">
                        {rv.duration}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {rv.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {rv.views >= 10000 ? `${(rv.views / 10000).toFixed(1)}万` : rv.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {rv.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}